import axios from 'axios';
import get from 'lodash.get';
import { from } from 'rxjs';

import { getOrderStatusFromViewName } from '../../services/api/order-status-from-view-service';
import { PAGE_SIZE_250, FIRST_PAGE } from '../../services/pagination-service';

/**
 * @typedef {import('./orders-api-types.d.ts').ManualOrderRequest} ManualOrderRequest
 */

/**
 * @typedef {import('rxjs').Observable} Observable
 */

export const getOrders = (orderStatus, page = FIRST_PAGE, pageSize = PAGE_SIZE_250, extraParams = {}) => {
  const params = {
    page,
    pageSize,
    orderGridStatus: getOrderStatusFromViewName(orderStatus),
  };
  const getAndTransform = axios.post('/ordergrid', { ...params, ...extraParams }).then((response) => get(response, 'data', { orders: [] }));

  const orders$ = from(getAndTransform);
  return orders$;
};

export const getOrdersInShipmentsMode = (
  orderStatus,
  pageNumber = FIRST_PAGE,
  pageSize = PAGE_SIZE_250,
  extraParams = {},
  includeQueryCount = true,
) => {
  const params = {
    page: {
      pageNumber,
      pageSize,
    },
  };
  const getAndTransform = axios
    .post('/ordergrid/shipmentmode/simple', {
      ...params,
      ...extraParams,
      filter: {
        ...extraParams.filter,
        assignedTo: extraParams.filter.assignedTos,
        orderGridStatus: getOrderStatusFromViewName(orderStatus),
      },
      includeQueryCount,
    })
    .then((response) => get(response, 'data', { orders: [] }));

  const orders$ = from(getAndTransform);
  return orders$;
};

export const markOrderAsPaid = (paymentDetails) => {
  const post = axios.post('salesOrder/markPaid', {
    data: paymentDetails,
  });

  return from(post);
};

/**
 * @param {ManualOrderRequest} request
 * @returns {Promise<AxiosResponse<object>>}
 */
export const createSalesOrderPromise = (request) => {
  return axios.post('salesorder', request);
};

/**
 * @param {ManualOrderRequest} newOrder
 * @returns {Observable<AxiosResponse<object>>}
 */
export const createOrder = (newOrder) => {
  const { amountSummary } = newOrder;
  const fixedAmmountSummary = {};
  Object.keys(amountSummary).forEach((key) => {
    const originalObject = amountSummary[key];
    fixedAmmountSummary[key] = {
      ...originalObject,
      value: Number.parseFloat(originalObject.value),
    };
  });
  const normalizedOrder = {
    ...newOrder,
    amountSummary: fixedAmmountSummary,
  };

  const post = createSalesOrderPromise(normalizedOrder);

  const order$ = from(post);
  return order$;
};

export const updateOrder = (updatedOrder) => {
  const { salesOrderId } = updatedOrder;
  const put = axios.put(`salesorder/${salesOrderId}/summary/edit`, updatedOrder);

  const order$ = from(put);
  return order$;
};

export const updateOrderPromise = (updatedOrder) => {
  const { salesOrderId } = updatedOrder;
  const put = axios.put(`salesorder/${salesOrderId}/summary/edit`, updatedOrder);

  return put;
};

export const cancelOrder = (salesOrderId) => {
  const post = axios.post(`salesOrder/${salesOrderId}/cancel`);

  return from(post);
};

export const deactivateFulfillmentPlans = (fulfillmentPlanIds) => {
  return axios.post('fulfillmentplan/deactivate', fulfillmentPlanIds);
};

export const updateShipTo = (shipToAddress, salesOrderId) => {
  const post = axios.post(`salesOrder/${salesOrderId}/SetShipTo`, shipToAddress);
  return from(post);
};

export const updateSoldTo = (soldTo, salesOrderId) => {
  const post = axios.post(`/SalesOrder/${salesOrderId}/SetSoldTo`, soldTo);
  return from(post);
};

export const getOrdersFromQuickSearchInShipmentMode = ({
  orderGridStatus,
  page: pageNumber,
  pageSize,
  searchTerm,
  storeGuids,
  orderBy,
  orderByDirection,
}) => {
  const params = {
    orderGridStatus,
    page: {
      pageNumber,
      pageSize,
    },
    searchTerm: searchTerm.trim(),
    orderBy,
    orderByDirection,
  };

  if (storeGuids) {
    params.storeGuids = [storeGuids];
  }

  const search$ = axios.post('ordergrid/shipmentmode/quicksearch', params).then((response) => get(response, 'data', {}));
  return from(search$);
};

export const restoreOrders = (salesOrderIds) => {
  const restore = axios.post('salesOrder/restore', salesOrderIds);
  return from(restore);
};

export const restoreFulfillments = (fulfillmentPlanIds) => {
  const restore = axios.post('fulfillmentplan/RestoreFromCanceled', fulfillmentPlanIds);
  return from(restore);
};

export const restoreCancelledShipment = (fulfillmentPlanIds) => {
  return axios.post('fulfillmentplan/RestoreFromCanceled', {
    fulfillmentPlanIds,
  });
};

export const restoreSingleShipment = (fulfillmentPlanId) => {
  return axios.post(`fulfillmentplan/restore/${fulfillmentPlanId}`, fulfillmentPlanId);
};

export const setHoldUntilDate = (fulfillmentPlanIds, holdUntil) => {
  const post = axios.post('/fulfillmentplan/SetHoldUntil', {
    fulfillmentPlanIds,
    holdUntil,
  });
  const holdUntilDateResponse$ = from(post);
  return holdUntilDateResponse$;
};

export const salesOrderIdToFulfillmentPlanIds = (salesOrderIds) => {
  const getAndTransform = axios.post('/SalesOrder/fulfillmentPlanIds', salesOrderIds).then((response) => response.data);
  const get$ = from(getAndTransform);
  return get$;
};

export const getOrdersCounts = () =>
  from(
    axios.get('/ordergrid/shipmentmode/Statuses').then((response) => {
      return {
        ...response.data,
        statusListByStore: response.data.statusListByStore.map((statusInfo) => {
          return {
            ...statusInfo,
            // this is returning an orderGridStatus as the derivedStatus
            orderGridStatus: statusInfo.derivedStatus,
          };
        }),
      };
    }),
  );

export const getSearchTermCountShipmentMode = (searchData) => {
  const searchParams = new URLSearchParams({
    ...searchData,
    searchTerm: searchData.searchTerm.trim(),
  });
  const search = axios.post(`/ordergrid/shipmentmode/quicksearch/statuses?${searchParams}`).then(({ data = {} }) => ({
    ...data,
    statusListByStore: data.statusListByStore.map((status) => ({
      ...status,
      orderGridStatus: status.statusCode,
    })),
  }));
  return from(search);
};

export const assignBulkFulfillmentPlanToUserFromOrdersGrid = (fulfillmentPlanIds, userId) => {
  const postAddress = axios.post('/fulfillmentplan/assignUserBulk', {
    fulfillmentPlanIds,
    userId,
  });

  const response$ = from(postAddress);
  return response$;
};

export const assignFulfillmentPlanToUserFromOrdersGrid = (fulfillmentPlanId, userId) => {
  const postAddress = axios.post('/fulfillmentplan/assignUserBulk', {
    fulfillmentPlanIds: [fulfillmentPlanId],
    userId,
  });

  const response$ = from(postAddress);
  return response$;
};

export const cancelFulfillmentPlansPromise = (fulfillmentPlanIds) => {
  return axios.post('/fulfillmentplan/Cancel', fulfillmentPlanIds).then((response) => response.data);
};

export const cancelFulfillmentPlans = (fulfillmentPlanIds) => {
  return from(axios.post('/fulfillmentplan/Cancel', fulfillmentPlanIds).then((response) => response.data));
};

export const cancelFulfillmentPlansAndVoidFulfillments = (fulfillmentPlanIds) => {
  return from(axios.post('/fulfillmentplan/CancelOrderAndVoidFulfillments', fulfillmentPlanIds).then((response) => response.data));
};

export const getGroupByCounts = (orderStatus, filter, groupByParams) => {
  return from(
    axios
      .post('/ordergrid/shipmentmode/groupby/counts', {
        filter: {
          ...filter,
          assignedTo: filter.assignedTos,
          orderGridStatus: getOrderStatusFromViewName(orderStatus),
        },
        groupBy: groupByParams,
      })
      .then((response) => response.data),
  );
};

export const getOrdersForOrderNumber = (orderNumber) => {
  const req = axios.get(`/order/search/orderNumber?orderNumber=${encodeURIComponent(orderNumber)}`).then((res) => res.data);
  return from(req);
};

export const getByFulfillmentPlanIds = (fulfillmentPlanIds = []) => {
  return axios.post('/order/search/fulfillmentPlanIds', fulfillmentPlanIds).then((response) => {
    return get(response, 'data');
  });
};
